import { API_ENDPOINTS } from '../config/api.config';
import {
  DEFAULT_PEER_REVIEW_REMINDER_MINUTES,
  MAX_PEER_REVIEW_LEVELS,
} from '../constants/peerReviewChain.constants';
import type {
  PeerReviewChainFormState,
  PeerReviewLevelFormState,
  PeerReviewMatrix,
  PeerReviewMatrixApiResponse,
  PeerReviewMatrixUpsertPayload,
} from '../types/peerReviewChain.types';
import { ApiError, apiGet, apiPut, extractApiErrorMessage } from '../utils/apiClient';

function extractErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError) {
    return extractApiErrorMessage(error.response, error.message || fallback);
  }
  return error instanceof Error ? error.message : fallback;
}

function createClientId(): string {
  const cryptoApi = globalThis.crypto;
  if (cryptoApi?.randomUUID) {
    return `pr-level-${cryptoApi.randomUUID().replaceAll('-', '').slice(0, 12)}`;
  }

  const bytes = new Uint8Array(8);
  cryptoApi.getRandomValues(bytes);
  const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
  return `pr-level-${hex}`;
}

export function buildDefaultPeerReviewChain(): PeerReviewChainFormState {
  const levels: PeerReviewLevelFormState[] = Array.from(
    { length: MAX_PEER_REVIEW_LEVELS },
    (_, index) => ({
      clientId: createClientId(),
      levelNumber: index + 1,
      reviewerId: null,
      timerMinutes: 0,
    })
  );

  return {
    reminderIntervalMinutes: DEFAULT_PEER_REVIEW_REMINDER_MINUTES,
    levels,
  };
}

export function mapMatrixToForm(matrix: PeerReviewMatrix | null): PeerReviewChainFormState {
  if (!matrix || !Array.isArray(matrix.reviewers) || !matrix.reviewers.length) {
    return buildDefaultPeerReviewChain();
  }

  const sorted = [...matrix.reviewers].sort((a, b) => a.order_index - b.order_index);
  const byOrder = new Map(sorted.map(row => [row.order_index, row]));

  const levels: PeerReviewLevelFormState[] = Array.from(
    { length: MAX_PEER_REVIEW_LEVELS },
    (_, index) => {
      const levelNumber = index + 1;
      const saved = byOrder.get(levelNumber);
      return {
        clientId: createClientId(),
        levelNumber,
        reviewerId: saved?.peer_reviewer_id ?? null,
        timerMinutes: saved?.review_minutes ?? 0,
      };
    }
  );

  return {
    reminderIntervalMinutes: Math.max(
      1,
      Number(matrix.reminder_interval_minutes) || DEFAULT_PEER_REVIEW_REMINDER_MINUTES
    ),
    levels,
  };
}

export function buildUpsertPayload(
  chain: PeerReviewChainFormState,
  facilityId?: number
): PeerReviewMatrixUpsertPayload {
  const reviewers = chain.levels
    .filter((level): level is typeof level & { reviewerId: number } => level.reviewerId != null)
    .map(level => ({
      peer_reviewer_id: level.reviewerId,
      review_minutes: level.timerMinutes,
    }));

  const payload: PeerReviewMatrixUpsertPayload = {
    reminder_interval_minutes: chain.reminderIntervalMinutes,
    reviewers,
  };

  if (facilityId != null) {
    payload.facility_id = facilityId;
  }

  return payload;
}

export async function getPeerReviewChain(facilityId: number): Promise<{
  success: boolean;
  data: PeerReviewChainFormState;
  matrix: PeerReviewMatrix | null;
  error?: string;
}> {
  try {
    const response = await apiGet<PeerReviewMatrixApiResponse>(
      API_ENDPOINTS.PEER_REVIEW_MATRIX.GET({ facility_id: facilityId }),
      { requireAuth: true }
    );
    const body = response.data;
    const matrix = body?.data ?? null;
    return {
      success: true,
      data: mapMatrixToForm(matrix),
      matrix,
    };
  } catch (error) {
    return {
      success: false,
      data: buildDefaultPeerReviewChain(),
      matrix: null,
      error: extractErrorMessage(error, 'Failed to load peer review matrix'),
    };
  }
}

export async function savePeerReviewChain(
  facilityId: number,
  chain: PeerReviewChainFormState
): Promise<{
  success: boolean;
  data?: PeerReviewChainFormState;
  matrix?: PeerReviewMatrix | null;
  error?: string;
}> {
  try {
    const response = await apiPut<PeerReviewMatrixApiResponse>(
      API_ENDPOINTS.PEER_REVIEW_MATRIX.UPSERT,
      buildUpsertPayload(chain, facilityId),
      { requireAuth: true }
    );
    const matrix = response.data?.data ?? null;
    return {
      success: true,
      data: mapMatrixToForm(matrix),
      matrix,
    };
  } catch (error) {
    return {
      success: false,
      error: extractErrorMessage(error, 'Failed to save peer review matrix'),
    };
  }
}
